import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { useProject } from "../api/project";
import { useMap } from "../contexts/MapContext";
import {
  PROJECT_LAYERS,
  PROJECT_LAYER_LABELS,
  type LayerStatusEntry,
  type ProjectLayer,
  type ProjectLayerStatus,
} from "../types/project";

// The queries each layer feeds, refreshed the moment that layer lands so the
// map fills in without a reload. The project query itself is already polling
// while anything is loading (useProject).
const QUERIES_BY_LAYER: Record<ProjectLayer, (projectId: string) => unknown[][]> = {
  vegetation: () => [["vegetationSources"], ["strategy"], ["strategyJob"], ["strategyStatus"]],
  weather: (id) => [
    ["weatherHistory", id], ["weatherHistoryDateRange", id], ["weatherStatistics", id],
    ["monthlyWeatherStatistics", id], ["dailyFDI", id], ["windDirectionFrequency", id],
    ["weatherPresets", id], ["climateExtremeScenarios", id], ["monthlyClimateProjections", id],
  ],
  canopy: () => [["strategy"], ["strategyJob"], ["strategyStatus"]],
  fire_history: (id) => [[id, "fireHistory"]],
  assets: (id) => [[id, "assets"], [id, "asset-counts"], [id, "asset-tiles-url"]],
  treatments: () => [["strategy", "treatments"], ["strategy"]],
  properties: (id) => [["project", id]],
  curing: (id) => [["curing", String(id)]],
};

// What each layer unlocks, so the wait is about something concrete.
const LAYER_HINTS: Record<ProjectLayer, string> = {
  vegetation: "fuel and simulations",
  weather: "charts, presets, fire danger",
  canopy: "crown fire",
  fire_history: "past fire scars",
  assets: "assets on the map",
  treatments: "from your sources",
  properties: "property boundaries",
  curing: "grass curing",
};

// How long the panel stays up after the last layer lands.
const DONE_LINGER_MS = 6000;

function describe(layer: ProjectLayer, entry: LayerStatusEntry): string {
  if (entry.state === "ready") return "Loaded";
  if (entry.state === "failed") return entry.error ? `Couldn't load: ${entry.error}` : "Couldn't load";
  if (entry.state === "loading") {
    const progress = entry.total && entry.total > 1 ? ` · ${entry.done ?? 0} of ${entry.total} tiles` : "";
    return `Loading${progress}`;
  }
  return `Waiting · ${LAYER_HINTS[layer]}`;
}

function StateIcon({ state }: { state: LayerStatusEntry["state"] }) {
  if (state === "ready") return <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />;
  if (state === "failed") return <ErrorOutlineIcon sx={{ fontSize: 18, color: "#dc2626" }} />;
  if (state === "loading") return <CircularProgress size={14} thickness={5} />;
  return <ScheduleIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
}

/** A small panel over the map while a freshly created (or expanded) project
 *  still loads data behind it. Lists every layer with a tick, a spinner or a
 *  wait icon and says what each one unlocks, so it is obvious the project is
 *  already usable and what is still on its way. Collapses to one line, and
 *  disappears a few seconds after the last layer lands. */
export default function LayerStatusBar() {
  const { project } = useProject();
  const { map } = useMap();
  const queryClient = useQueryClient();
  const projectId = project?.data?.id ? String(project.data.id) : null;
  const layerStatus = project?.data?.layer_status;
  const status: ProjectLayerStatus = useMemo(() => layerStatus ?? {}, [layerStatus]);
  const prev = useRef<ProjectLayerStatus>({});
  const [collapsed, setCollapsed] = useState(false);
  const [lingering, setLingering] = useState(false);

  // Refresh what a layer feeds when it goes from loading to ready.
  useEffect(() => {
    if (!projectId) return;
    for (const layer of PROJECT_LAYERS) {
      const was = prev.current[layer]?.state;
      const now = status[layer]?.state;
      if (now === "ready" && was && was !== "ready") {
        for (const key of QUERIES_BY_LAYER[layer](projectId)) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    }
    prev.current = status;
  }, [status, projectId, queryClient]);

  const rows = PROJECT_LAYERS.filter((layer) => {
    const state = status[layer]?.state;
    return !!state && state !== "skipped";
  });
  const pending = rows.filter((l) => status[l]!.state === "pending" || status[l]!.state === "loading");
  const failed = rows.filter((l) => status[l]!.state === "failed");
  const done = rows.length - pending.length;
  const everythingIn = pending.length === 0;

  // Keep the panel up briefly once everything has landed, then hide it. A
  // failed layer keeps it visible so the problem isn't lost.
  useEffect(() => {
    if (!everythingIn || failed.length > 0) {
      setLingering(false);
      return;
    }
    if (done === 0) return;
    setLingering(true);
    const timer = setTimeout(() => setLingering(false), DONE_LINGER_MS);
    return () => clearTimeout(timer);
  }, [everythingIn, failed.length, done]);

  const wasEverLoading = useRef(false);
  if (pending.length > 0) wasEverLoading.current = true;

  // Drawn inside the map's own container (like the expansion overlay), so
  // "top" means the top of the map and not the top of the page, where the
  // strategy and stats bars would cover it.
  const host = map?.getCanvas().parentElement ?? null;
  if (!host) return null;
  if (rows.length === 0) return null;
  if (everythingIn && failed.length === 0 && !(lingering && wasEverLoading.current)) return null;

  const title = everythingIn
    ? failed.length > 0
      ? `${failed.length} ${failed.length === 1 ? "layer" : "layers"} couldn't load`
      : "All project data loaded"
    : `Project is open · loading ${pending.length} more ${pending.length === 1 ? "layer" : "layers"}`;

  return createPortal(
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        width: 380,
        maxWidth: "calc(100% - 32px)",
        zIndex: 11,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.97)",
        textAlign: "left",
        color: "#111827",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, cursor: "pointer" }}
        onClick={() => setCollapsed((c) => !c)}
      >
        {everythingIn ? (
          <StateIcon state={failed.length > 0 ? "failed" : "ready"} />
        ) : (
          <CircularProgress size={16} thickness={5} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          {done}/{rows.length}
        </Typography>
        <IconButton size="small" aria-label={collapsed ? "Show layers" : "Hide layers"} sx={{ p: 0.25 }}>
          {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Box>
      {!everythingIn && (
        <LinearProgress variant="determinate" value={(done / rows.length) * 100} sx={{ height: 3 }} />
      )}
      <Collapse in={!collapsed}>
        <Box sx={{ px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 0.6 }}>
          {!everythingIn && (
            <Typography variant="caption" sx={{ color: "#4b5563", mb: 0.25 }}>
              You can use the map now. Each layer appears as soon as it loads.
            </Typography>
          )}
          {rows.map((layer) => {
            const entry = status[layer]!;
            const detail = describe(layer, entry);
            const row = (
              <Box key={layer} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                  <StateIcon state={entry.state} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ flex: "0 0 130px", color: entry.state === "pending" ? "#6b7280" : "inherit" }}
                >
                  {PROJECT_LAYER_LABELS[layer]}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ flex: 1, lineHeight: 1.25, color: entry.state === "failed" ? "#dc2626" : "#6b7280" }}
                >
                  {detail}
                </Typography>
              </Box>
            );
            return entry.state === "failed" && entry.error ? (
              <Tooltip key={layer} title={entry.error}>
                {row}
              </Tooltip>
            ) : (
              row
            );
          })}
        </Box>
      </Collapse>
    </Paper>,
    host,
  );
}
